import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { useMe } from '../me/use-me.js';
import { Modal } from './Modal.js';
import { PROVIDER_FIELDS, type FieldDef } from './providers/field-defs.js';
import { ProviderForm } from './providers/ProviderForm.js';
import {
  useConnection,
  useDeleteConnection,
  useRotateSecret,
  useSaveConnection,
  useTestConnection,
  type ProbeResult,
} from './use-integration.js';

const SUPPORTED = ['bamboohr', 'docusign', 'graph', 'jira', 'email'] as const;
type ProviderKey = (typeof SUPPORTED)[number];

function isProvider(s: string | undefined): s is ProviderKey {
  return !!s && SUPPORTED.includes(s as ProviderKey);
}

export function IntegrationDetailPage(): JSX.Element {
  const { companyId, provider, name } = useParams<{
    companyId: string;
    provider: string;
    name: string;
  }>();

  if (!companyId || !isProvider(provider) || !name) {
    return <p className="text-slate-500">Unknown connection.</p>;
  }

  return <Inner companyId={companyId} provider={provider} name={name} />;
}

function Inner({
  companyId,
  provider,
  name,
}: {
  companyId: string;
  provider: ProviderKey;
  name: string;
}): JSX.Element {
  const { me } = useMe();
  const { data, isLoading, error } = useConnection(companyId, provider, name);
  const [lastProbe, setLastProbe] = useState<ProbeResult | null>(null);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const save = useSaveConnection(companyId, provider, name);
  const test = useTestConnection(companyId, provider, name);
  const rotate = useRotateSecret(companyId, provider, name);
  const del = useDeleteConnection(companyId, provider, name);
  const navigate = useNavigate();

  const meta = PROVIDER_FIELDS[provider];
  const allFields: FieldDef[] = useMemo(() => [...meta.nonSecret, ...meta.secret], [meta]);

  // Email provider uses a synthetic `backend` select to switch between
  // SMTP and SendGrid modes. `backend` is NOT stored — it's derived on
  // load from the stored `config.host` sentinel, and collapsed on
  // submit before we hand the payload to the API.
  const initialFormValues = useMemo((): Record<string, unknown> => {
    const base: Record<string, unknown> = { ...(data?.config ?? {}) };
    if (provider === 'email') {
      const host = (data?.config as { host?: string } | null | undefined)?.host;
      base.backend = host === 'sendgrid' ? 'sendgrid' : 'smtp';
    }
    return base;
  }, [data, provider]);

  const role = useMemo(() => {
    const ionic = (me?.ionicRoles ?? []).length > 0;
    if (ionic) return 'admin' as const;
    const company = (me?.companies ?? []).find((c) => c.companyId === companyId);
    if (!company) return 'none' as const;
    if (company.roles.includes('company_admin')) return 'admin' as const;
    if (company.roles.includes('company_hr')) return 'hr' as const;
    return 'viewer' as const;
  }, [me, companyId]);

  const editable = role === 'admin';
  const canRead = role === 'admin' || role === 'hr';
  const isDefault = name === 'default';

  if (!canRead) {
    return (
      <div className="rounded-md border border-slate-200 bg-white p-4 text-slate-600">
        You don&rsquo;t have permission to view this connection.
      </div>
    );
  }

  if (isLoading) return <p className="text-slate-500">Loading…</p>;

  // `data === null` for a connection that doesn't exist yet — show an empty
  // form so HR can save for the first time.
  const hasStoredSecret = data != null && data.status !== 'unconfigured';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link
            to={`/companies/${companyId}/integrations`}
            className="text-sm text-brand-600 hover:text-brand-700"
          >
            ← Back to integrations
          </Link>
          <h1 className="mt-1 text-2xl font-semibold">
            {meta.title}
            <span className="ml-3 font-mono text-base font-normal text-slate-500">{name}</span>
            {isDefault && (
              <span className="ml-2 align-middle rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                default
              </span>
            )}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">{meta.description}</p>
        </div>
        {data && <StatusBadge status={data.status} />}
      </div>

      {error && <ErrorCallout title="Could not load connection" message={error.message} />}

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <ProviderForm
          fields={allFields}
          initialValues={initialFormValues}
          hasStoredSecret={hasStoredSecret}
          editable={editable}
          submitLabel={hasStoredSecret ? 'Save changes' : 'Save'}
          pending={save.isPending}
          onSubmit={async (values) => {
            const config: Record<string, unknown> = {};
            const secret: Record<string, unknown> = {};
            for (const f of allFields) {
              if (!(f.key in values)) continue;
              // `backend` is a UI-only discriminator for the email
              // provider — never persisted. It's mapped below into the
              // stored `host` sentinel ('sendgrid') or the real SMTP
              // host the admin typed.
              if (provider === 'email' && f.key === 'backend') continue;
              if ('secret' in f && f.secret) secret[f.key] = values[f.key];
              else config[f.key] = values[f.key];
            }
            // Email + SendGrid backend: overwrite `host` with the
            // sentinel. The rest of the form fields (SMTP host/port/…)
            // were hidden and therefore already absent from `values`,
            // so `config` only carries `fromAddress` (and the sentinel).
            if (provider === 'email' && values.backend === 'sendgrid') {
              config.host = 'sendgrid';
            }
            await save.mutateAsync({
              config,
              ...(Object.keys(secret).length > 0 ? { secret } : {}),
            });
          }}
        />
        {save.error && <ErrorCallout title="Save failed" message={save.error.message} inline />}
      </section>

      {data && hasStoredSecret && editable && (
        <section className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4">
          <button
            type="button"
            onClick={() => {
              setLastProbe(null);
              test.mutate(undefined, {
                onSuccess: (res) => setLastProbe(res),
              });
            }}
            disabled={test.isPending}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            {test.isPending ? 'Testing…' : 'Test connection'}
          </button>
          <button
            type="button"
            onClick={() => setRotateOpen(true)}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50"
          >
            Rotate secret
          </button>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            disabled={isDefault}
            title={isDefault ? 'The default connection cannot be deleted.' : undefined}
            className={clsx(
              'ml-auto rounded-md border px-3 py-1.5 text-sm font-medium',
              isDefault
                ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                : 'border-red-300 text-red-700 hover:bg-red-50',
            )}
          >
            Delete
          </button>

          {lastProbe && (
            <div className="mt-2 w-full">
              {lastProbe.ok ? (
                <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
                  ✓ {lastProbe.message ?? 'Connection ok'}
                </p>
              ) : (
                <ErrorCallout
                  title={`Probe failed${lastProbe.status ? ` (HTTP ${lastProbe.status})` : ''}`}
                  message={lastProbe.reason}
                  inline
                />
              )}
            </div>
          )}
        </section>
      )}

      {data && (
        <section className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
          <dl className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <InfoRow label="Status" value={data.status} />
            <InfoRow label="Last tested" value={formatDate(data.lastTestedAt)} />
            <InfoRow label="Last rotated" value={formatDate(data.lastRotatedAt)} />
          </dl>
          {data.lastTestError && (
            <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
              Last test error: {data.lastTestError}
            </p>
          )}
        </section>
      )}

      <RotateModal
        open={rotateOpen}
        onClose={() => setRotateOpen(false)}
        fields={meta.secret}
        pending={rotate.isPending}
        onSubmit={async (values) => {
          await rotate.mutateAsync({ secret: values });
          setRotateOpen(false);
        }}
      />

      <DeleteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={`${meta.title} / ${name}`}
        pending={del.isPending}
        onConfirm={async () => {
          await del.mutateAsync();
          setDeleteOpen(false);
          navigate(`/companies/${companyId}/integrations`);
        }}
      />
    </div>
  );
}

function RotateModal({
  open,
  onClose,
  fields,
  pending,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  fields: FieldDef[];
  pending: boolean;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
}): JSX.Element {
  return (
    <Modal open={open} onClose={onClose} title="Rotate secret">
      <p className="mb-4 text-sm text-slate-600">
        Enter the new secret value(s) below. The previous version is retained by AWS Secrets Manager
        for 7 days if you need to roll back.
      </p>
      <ProviderForm
        fields={fields}
        initialValues={{}}
        hasStoredSecret={false}
        editable
        submitLabel="Rotate"
        pending={pending}
        onSubmit={onSubmit}
      />
    </Modal>
  );
}

function DeleteModal({
  open,
  onClose,
  title,
  pending,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  pending: boolean;
  onConfirm: () => Promise<void>;
}): JSX.Element {
  const [typed, setTyped] = useState('');
  return (
    <Modal open={open} onClose={onClose} title={`Delete ${title}?`}>
      <p className="mb-3 text-sm text-slate-600">
        The connection row is deleted immediately. The stored secret is scheduled for deletion with
        a 7-day recovery window — you can restore within that window via the runbook.
      </p>
      <label className="block text-sm font-medium text-slate-800">
        Type <span className="font-mono">{title}</span> to confirm:
      </label>
      <input
        type="text"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
      />
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void onConfirm()}
          disabled={pending || typed !== title}
          className={clsx(
            'rounded-md border border-red-400 bg-red-600 px-3 py-1.5 text-sm font-medium text-white',
            'hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {pending ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </Modal>
  );
}

function StatusBadge({
  status,
}: {
  status: 'unconfigured' | 'connected' | 'failing';
}): JSX.Element {
  const styles: Record<typeof status, string> = {
    unconfigured: 'bg-slate-100 text-slate-700',
    connected: 'bg-green-100 text-green-800',
    failing: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800">{value ?? '—'}</dd>
    </div>
  );
}

function ErrorCallout({
  title,
  message,
  inline,
}: {
  title: string;
  message: string;
  inline?: boolean;
}): JSX.Element {
  return (
    <div
      className={clsx(
        'rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800',
        inline && 'mt-3',
      )}
    >
      <p className="font-medium">{title}</p>
      <p>{message}</p>
    </div>
  );
}

function formatDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleString();
}
